import React from "react";
import { STEPS, ROLE_LABELS } from "@/lib/steps";
import { CheckCircle2, Circle, Loader2, AlertOctagon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const statusIcon = (status) => {
  if (status === "completed") return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
  if (status === "in_progress") return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
  if (status === "blocked") return <AlertOctagon className="w-4 h-4 text-red-600" />;
  return <Circle className="w-4 h-4 text-slate-400" />;
};

const nodeClass = (status) => {
  if (status === "completed") return "bg-emerald-600 text-white border-emerald-600 shadow-sm shadow-emerald-600/20";
  if (status === "in_progress") return "bg-primary text-white border-primary shadow-md shadow-primary/25 ring-[3px] ring-primary/15";
  if (status === "blocked") return "bg-red-600 text-white border-red-600 shadow-sm shadow-red-600/20";
  return "bg-white text-slate-400 border-slate-200";
};

const connectorClass = (leftStatus) => {
  if (leftStatus === "completed") return "bg-emerald-500";
  return "bg-slate-200";
};

export default function StepPipeline({ steps, compact = false, onStepClick }) {
  // Ensure we have all steps from STEPS, even ones not yet materialized on the program
  const stepMap = new Map((steps || []).map((s) => [s.step_number, s]));
  const merged = STEPS.map((meta) => {
    const s = stepMap.get(meta.number);
    return { ...meta, status: s?.status || "pending", data: s };
  });

  return (
    <TooltipProvider delayDuration={100}>
      <div className="overflow-x-auto pb-2">
        <div className={cn("flex items-center min-w-max", compact ? "gap-0" : "gap-0")}>
          {merged.map((s, idx) => (
            <React.Fragment key={s.number}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onStepClick && onStepClick(s.number)}
                    data-testid={`pipeline-step-${s.number}`}
                    className="flex flex-col items-center group focus:outline-none"
                  >
                    <div
                      className={cn(
                        "w-9 h-9 rounded-full grid place-items-center border text-xs font-semibold transition-all duration-300 ease-flowy",
                        nodeClass(s.status),
                        "group-hover:scale-110"
                      )}
                    >
                      {s.status === "completed" ? <CheckCircle2 className="w-4 h-4" /> : s.number}
                    </div>
                    {!compact && (
                      <div className="mt-2 text-center max-w-[86px]">
                        <div className="text-[11px] font-medium text-slate-800 leading-tight">{s.short}</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                          {s.owner}
                        </div>
                      </div>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <div className="text-xs font-semibold">Step {s.number}: {s.title}</div>
                  <div className="text-[11px] text-muted-foreground">Owner: {ROLE_LABELS[s.owner]}</div>
                  <div className="text-[11px] mt-1 flex items-center gap-1">{statusIcon(s.status)} {s.status.replace("_", " ")}</div>
                  {s.data?.due_date && <div className="text-[11px] mt-1">Due: {new Date(s.data.due_date).toLocaleDateString()}</div>}
                </TooltipContent>
              </Tooltip>
              {idx < merged.length - 1 && (
                <div className={cn("h-[3px] w-8 rounded-full transition-colors duration-300", connectorClass(s.status))} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
